import { Fragment, createContext, useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, BookOpen, Check, ChevronLeft, ChevronRight, Lightbulb, X, Pause, Scale, Users, BarChart3 } from 'lucide-react'
import { Badge, Button, Drawer, fmtDate } from '../../../kit'
import { OUTCOME_BY_CODE, outcomePillCls } from '../../../data/implantOutcomes.js'
import { presetById, VIEWS } from '../../../lib/implant.js'
import { REVIEW_STATUSES } from '../../../lib/implantReview.js'
import {
  GUIDE_UPDATED, ROLES, THE_RULE, STEPS, DOORS, LIST_IN, VIEWS_NOTE, LIST_OUT, LIST_ORDER, PICK,
  REVIEW, CALL, HOLD_TYPES, HOLD_MAIN_HINT, HOLD_NOT, HOLD, VISIT, DONE, NEXT_ROUND, COUNTING, MUST, NEVER,
  MAP_BOXES, MAP_LABELS, MAP_LEGEND,
} from '../../../data/implantGuide.js'

/* ────────────────────────────────────────────────────────────────────────────
 * วิธีทำงาน — the implant section's last tab: the staff guideline, drawn as one map
 * from top to bottom. Every word is in data/implantGuide.js; this file only lays it
 * out. Chips, list names, views and review statuses are read from the same files the
 * real buttons use, so the guide can never name a button that is not on the screen.
 *
 * The map (Ram, 2026-10-29: "look similar to the image… make it vertical") is the
 * journey map of implant/recall-journey.html turned top to bottom: boxes on a spine,
 * side boxes for where a patient waits or stops, solid arrows forward, dashed arrows
 * for the ways back onto the list, a numbered badge per step. Every box is a button —
 * it opens the kit Drawer on that step (and scrolls to the branch the box stands for):
 * what to do, then ทำไมต้องทำแบบนี้. The drawer's footer walks to the next step, which
 * is how อ่านทีละขั้น reads the whole guide in order.
 *
 * Coordinates are hand-placed in one 632-wide viewBox (BOX, EDGES, LABELS below); no
 * two lines cross. Move a box → move the edges that touch it.
 *
 * ?step=<key> opens the drawer on one step — a link a manager can send. Static on
 * purpose: no API call, so anyone who can open the page can read it, including staff
 * the call list itself refuses.
 * ──────────────────────────────────────────────────────────────────────────── */

const NUM = Object.fromEntries(STEPS.map((s, i) => [s.key, i + 1]))
const STEP = Object.fromEntries(STEPS.map((s) => [s.key, s]))
const DOOR = Object.fromEntries(DOORS.map((d) => [d.key, d]))
const anchor = (key, sub) => (sub ? `guide-${key}-${sub}` : `guide-${key}`)

// open(step, branch?) — show that step in the drawer, scrolled to the branch
const OpenCtx = createContext(() => {})

/** `**word**` → bold. The only markup the content file uses. */
function Rich({ text }) {
  return String(text).split('**').map((p, i) => (i % 2
    ? <strong key={i} className="font-semibold text-slate-800">{p}</strong>
    : <Fragment key={i}>{p}</Fragment>))
}

function Bullets({ items }) {
  return (
    <ul className="space-y-1">
      {items.map((t, i) => (
        <li key={i} className="flex gap-2 text-[13px] leading-relaxed text-slate-600">
          <span className="mt-[8px] h-1 w-1 flex-none rounded-full bg-slate-400" />
          <div className="min-w-0"><Rich text={t} /></div>
        </li>
      ))}
    </ul>
  )
}

function Warn({ text }) {
  return (
    <p className="mt-2 flex items-start gap-1.5 text-[12.5px] leading-relaxed text-rose-700">
      <AlertTriangle size={13} className="mt-[3px] flex-none" />
      <span><Rich text={text} /></span>
    </p>
  )
}

/** The reason behind a step's rules. */
function Why({ items }) {
  return (
    <div className="rounded-xl bg-brand-50/70 px-4 py-3 ring-1 ring-inset ring-brand-100">
      <div className="mb-1 flex items-center gap-1.5 text-[13px] font-semibold text-brand-800">
        <Lightbulb size={15} />ทำไมต้องทำแบบนี้
      </div>
      <ul className="space-y-1.5">
        {items.map((t, i) => <li key={i} className="text-[13px] leading-relaxed text-slate-700"><Rich text={t} /></li>)}
      </ul>
    </div>
  )
}

function GoTo({ to }) {
  const open = useContext(OpenCtx)
  return (
    <button type="button" onClick={() => open(to)}
      className="inline-flex items-center gap-1 text-[12.5px] font-medium text-brand-700 hover:underline">
      → ข้อ {NUM[to]} {STEP[to].title}
    </button>
  )
}

function Who({ roles }) {
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((r) => <Badge key={r} color={ROLES[r].color}>{ROLES[r].label}</Badge>)}
    </div>
  )
}

/** Outcome buttons exactly as the note box shows them (data/implantOutcomes.js). */
function Chips({ codes }) {
  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {codes.map((c) => {
        const o = OUTCOME_BY_CODE[c]
        return <span key={c} className={`rounded-full border px-2 py-0.5 text-[11.5px] ${outcomePillCls(o?.tone)}`}>{o?.thai || c}</span>
      })}
    </div>
  )
}

function ColHead({ icon: Icon, tone, children }) {
  return <div className={`mb-1.5 flex items-center gap-1.5 text-[13px] font-semibold ${tone}`}><Icon size={15} />{children}</div>
}

const BRANCH_TOP = { slate: 'border-t-slate-400', amber: 'border-t-amber-400', sky: 'border-t-sky-400', rose: 'border-t-rose-400', emerald: 'border-t-emerald-500' }

/** One way in or out of a step. The one the clicked box stands for is ringed. */
function Block({ k, b, active, children }) {
  return (
    <div id={anchor(k, b.key)}
      className={`scroll-mt-2 rounded-xl border border-t-4 border-slate-200 ${BRANCH_TOP[b.tone] || BRANCH_TOP.slate} bg-white p-3 ${active === b.key ? 'ring-2 ring-brand-300' : ''}`}>
      <h3 className="text-[13.5px] font-semibold text-slate-800">{b.title}</h3>
      {children}
    </div>
  )
}

function Branches({ k, items, active }) {
  return (
    <div className="mt-3 space-y-2.5">
      {items.map((b) => (
        <Block key={b.key} k={k} b={b} active={active}>
          {b.codes && <Chips codes={b.codes} />}
          <div className="mt-2"><Bullets items={b.do} /></div>
          {b.warn && <Warn text={b.warn} />}
          {b.result && <p className="mt-2 text-[12.5px] text-slate-500"><Rich text={b.result} /></p>}
          {b.to && <div className="mt-2"><GoTo to={b.to} /></div>}
        </Block>
      ))}
    </div>
  )
}

/** A part of a step with its own heading (who: in / waiting / out; done: closes / not). */
function Part({ id, active, children }) {
  return <div id={id} className={`scroll-mt-2 rounded-xl p-3 ${active ? 'bg-white ring-2 ring-brand-300' : 'bg-white ring-1 ring-slate-200'}`}>{children}</div>
}

function Panel({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="mb-2 flex items-center gap-2 text-[14.5px] font-semibold text-slate-900">
        <Icon size={16} className="text-brand-600" />{title}
      </h2>
      {children}
    </div>
  )
}

/** A row of pills joined by arrows (lists in work order, review statuses, call results). */
function Chain({ items }) {
  return (
    <div className="mb-2.5 flex flex-wrap items-center gap-1">
      {items.map((it, i) => (
        <Fragment key={it.key}>
          {i > 0 && <span className="text-[12px] text-slate-300">{it.sep || '→'}</span>}
          <span title={it.title} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] ring-1 ring-inset ${it.cls}`}>
            {it.dot && <span className={`h-1.5 w-1.5 rounded-full ${it.dot}`} />}{it.label}
          </span>
        </Fragment>
      ))}
    </div>
  )
}

const LIST_CHAIN = LIST_ORDER.map((id) => {
  const p = presetById(id)
  return {
    key: id, label: p?.label || id, title: p?.hint,
    cls: p?.warm ? 'bg-amber-50 text-amber-800 ring-amber-200'
      : p?.inQuarter ? 'bg-brand-50 text-brand-700 ring-brand-200' : 'bg-white text-slate-500 ring-slate-200',
  }
})
const REVIEW_CHAIN = REVIEW_STATUSES.map((s) => ({ key: s.id, label: s.label, title: s.hint, cls: s.pill, dot: s.dot, sep: s.id === 'fail' ? '·' : '→' }))
// the ผลการโทร tab's four tiles, in order (api 680) — what COUNTING defines
const RESULT_CHAIN = [
  { key: 'called', label: 'โทรแล้ว', cls: 'bg-slate-50 text-slate-600 ring-slate-200' },
  { key: 'reached', label: 'รับสาย', cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
  { key: 'booked', label: 'นัดได้', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  { key: 'paid', label: 'จ่ายแล้ว', cls: 'bg-emerald-600 text-white ring-emerald-600' },
]

/* ── what each step says in the drawer; `a` = the branch to ring ─────────────── */
const BODY = {
  doors: (a) => (
    <div className="space-y-2.5">
      {DOORS.map((d) => (
        <Block key={d.key} k="doors" b={d} active={a}>
          <div className="mt-1"><Who roles={d.who} /></div>
          <div className="mt-2"><Bullets items={d.do} /></div>
          {d.warn && <Warn text={d.warn} />}
          <div className="mt-2"><GoTo to={d.to} /></div>
        </Block>
      ))}
    </div>
  ),
  who: (a) => (
    <div className="space-y-2.5">
      <Part id={anchor('who', 'in')} active={a === 'in'}>
        <ColHead icon={Check} tone="text-emerald-700">เข้าลิสต์</ColHead>
        <Bullets items={LIST_IN} />
      </Part>
      <Part id={anchor('who', 'views')} active={a === 'views'}>
        <ColHead icon={Pause} tone="text-amber-700">ไม่ต้องโทรตอนนี้</ColHead>
        <ul className="space-y-1.5">
          {VIEWS.map((v) => (
            <li key={v.id} className="text-[13px] leading-relaxed text-slate-600">
              <span className="font-semibold text-slate-800">{v.label}</span> — {v.hint}
            </li>
          ))}
        </ul>
        <p className="mt-1.5 text-[12.5px] text-slate-500"><Rich text={VIEWS_NOTE} /></p>
      </Part>
      <Part id={anchor('who', 'out')} active={a === 'out'}>
        <ColHead icon={X} tone="text-rose-700">ไม่เข้าลิสต์ — ระบบซ่อนให้เอง</ColHead>
        <Bullets items={LIST_OUT} />
      </Part>
    </div>
  ),
  pick: () => (
    <Part>
      <Chain items={LIST_CHAIN} />
      <Bullets items={PICK.do} />
    </Part>
  ),
  review: () => (
    <Part>
      <Chain items={REVIEW_CHAIN} />
      <Bullets items={REVIEW.do} />
      <Warn text={REVIEW.warn} />
      <p className="mt-3 rounded-lg bg-rose-50/70 px-3 py-2 text-[12.5px] text-rose-800"><Rich text={REVIEW.fail} /></p>
    </Part>
  ),
  call: (a) => (
    <>
      <Part><Bullets items={CALL.do} /></Part>
      <Branches k="call" items={CALL.branches} active={a} />
      <div className="mt-3 space-y-1.5 px-1">
        {CALL.notes.map((t, i) => <p key={i} className="text-[12.5px] leading-relaxed text-slate-600"><Rich text={t} /></p>)}
      </div>
    </>
  ),
  hold: () => (
    <Part>
      <Bullets items={HOLD.do} />
      <Warn text={HOLD.warn} />
      <div className="mt-3">
        <h3 className="mb-1 text-[12.5px] font-medium text-slate-700">
          ประเภทนัดรากเทียม (ใช้ตอนลงนัด) <span className="font-normal text-slate-500">· {HOLD_MAIN_HINT}</span>
        </h3>
        <div className="flex flex-wrap gap-1">
          {HOLD_TYPES.map((t) => <Badge key={t.label} tone={t.main ? 'brand' : 'slate'}>{t.label}</Badge>)}
        </div>
        <p className="mt-1.5 text-[12px] text-slate-500">{HOLD_NOT}</p>
      </div>
    </Part>
  ),
  visit: (a) => (
    <>
      <Part><Bullets items={VISIT.do} /></Part>
      <Branches k="visit" items={VISIT.branches} active={a} />
    </>
  ),
  done: () => (
    <div className="space-y-2.5">
      <Part>
        <ColHead icon={Check} tone="text-emerald-700">บิลที่จบรอบ</ColHead>
        <Bullets items={DONE.closes} />
        <div className="mt-3 border-t border-slate-100 pt-2.5">
          <ColHead icon={X} tone="text-slate-600">ยังไม่จบรอบ</ColHead>
          <p className="text-[13px] leading-relaxed text-slate-600">{DONE.notClose}</p>
        </div>
      </Part>
      <Part><Bullets items={DONE.notes} /></Part>
    </div>
  ),
  next: () => (
    <Part>
      <Bullets items={NEXT_ROUND.do} />
      <table className="mt-3 w-full text-left text-[12.5px]">
        <thead>
          <tr className="border-b border-slate-200 text-slate-500">
            <th className="py-1.5 pr-3 font-medium">รอบที่แล้วจบแบบ</th>
            <th className="py-1.5 font-medium">กลับเข้าลิสต์เมื่อ</th>
          </tr>
        </thead>
        <tbody>
          {NEXT_ROUND.returns.map((r) => (
            <tr key={r.ended} className="border-b border-slate-100 align-top">
              <td className="py-1.5 pr-3 text-slate-700">{r.ended}</td>
              <td className="py-1.5 text-slate-600"><Rich text={r.back} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2"><GoTo to="doors" /></div>
    </Part>
  ),
}

/* ── the map ───────────────────────────────────────────────────────────────────
 * Lanes: left side x 44–204 · spine x 232–408 (centre 320) · right side x 440–600.
 * Gutters: x 28 the booking line from the two door boxes, x 12 the new-round loop,
 * x 618 the visit outcomes' way back. Heights: 72 = title + 2 lines, 56 = + 1, 44 small.
 */
const BOX = {
  lead:   [44, 28, 160, 72],
  ext:    [232, 28, 176, 72],
  chair:  [44, 120, 160, 72],
  who:    [232, 132, 176, 56],
  out:    [440, 132, 160, 56],
  pick:   [232, 228, 176, 56],
  review: [232, 324, 176, 72],
  stop:   [440, 392, 160, 56],
  call:   [232, 452, 176, 72],
  park:   [44, 480, 160, 56],
  hold:   [232, 600, 176, 56],
  visit:  [232, 716, 176, 56],
  noshow: [440, 694, 160, 44],
  unpaid: [440, 746, 160, 44],
  graft:  [440, 798, 160, 44],
  done:   [232, 866, 176, 56],
  next:   [232, 966, 176, 56],
}
const VIEW_H = 1036

// go = forward (solid, arrow) · back = onto the list again (dashed brand, arrow) · tie = no arrow
const EDGES = [
  ['go', 'M44 64 H28 M44 156 H28 M28 64 V628 H230'],       // lead / chair → booked → hold
  ['go', 'M320 100 V130'],                                 // extraction → the list
  ['go', 'M408 160 H438'],                                 // → not on the list
  ['go', 'M320 188 V226'],
  ['go', 'M320 284 V322'],
  ['go', 'M232 256 H212 V470 H230'],                       // W skips review
  ['go', 'M320 396 V450'],                                 // พร้อมโทร
  ['go', 'M408 360 H520 V390'],                            // ไม่ผ่าน
  ['go', 'M408 470 H520 V450'],                            // ไม่ใช่เคสเรา
  ['go', 'M232 508 H206'],                                 // ยังไม่พร้อม → park
  ['go', 'M320 524 V598'],                                 // จองแล้ว
  ['go', 'M320 656 V714'],                                 // the day
  ['tie', 'M408 744 H424 M424 716 V820'],
  ['go', 'M424 716 H438'],
  ['go', 'M424 768 H438'],
  ['go', 'M424 820 H438'],
  ['go', 'M320 772 V864'],                                 // มา + จ่าย
  ['go', 'M320 922 V964'],
  ['back', 'M408 494 C438 494 438 514 410 514'],           // no answer → call again
  ['back', 'M124 536 V552 H290 V526'],                     // 6 months → พร้อมโทร
  ['back', 'M600 716 H618 M600 768 H618 M600 820 H618 M618 820 V560 H392 V526'],
  ['back', 'M262 656 C262 686 292 686 292 658'],           // came, booked the next stage
  ['back', 'M232 994 H12 V12 H300 V26'],                   // a new round
]
const JOINS = [['go', 28, 156], ['go', 424, 744], ['back', 618, 716], ['back', 618, 768]]

// [x, y, label key, anchor, kind]
const LABELS = [
  [40, 620, 'booked', 'start', 'go'],
  [204, 364, 'skip', 'end', 'go'],
  [328, 428, 'ready', 'start', 'go'],
  [464, 352, 'fail', 'middle', 'go'],
  [528, 464, 'notOurs', 'start', 'go'],
  [444, 509, 'retry', 'start', 'back'],
  [136, 568, 'sixMonths', 'start', 'back'],
  [328, 552, 'bk', 'start', 'go'],
  [254, 684, 'nextStage', 'end', 'back'],
  [328, 692, 'day', 'start', 'go'],
  [505, 552, 'back', 'middle', 'back'],
  [328, 822, 'paid', 'start', 'go'],
  [40, 986, 'round', 'start', 'back'],
]

const STROKE = { go: 'stroke-slate-400', tie: 'stroke-slate-400', back: 'stroke-brand-500' }
const DOT = { rose: 'fill-rose-500', amber: 'fill-amber-500', sky: 'fill-sky-500', emerald: 'fill-emerald-500', slate: 'fill-slate-400' }

function MapBox({ id, sel }) {
  const open = useContext(OpenCtx)
  const b = MAP_BOXES[id]
  const [x, y, w, h] = BOX[id]
  // A step's own box gets the step badge and its people; a side box (where a patient
  // waits or stops) is a branch of the step above it and gets neither.
  const own = !b.branch || b.step === 'doors'
  const roles = !own ? [] : b.step === 'doors' ? DOOR[b.branch].who : STEP[b.step].who
  const on = sel?.step === b.step && (sel.branch || null) === (b.branch || null)
  const ty = y + (h === 44 ? 19 : h === 56 ? 23 : 25)
  const gap = h === 44 ? 17 : 19
  const go = () => open(b.step, b.branch)
  return (
    <g role="button" tabIndex={0} aria-label={`${own ? `ข้อ ${NUM[b.step]} ` : ''}${b.title} — ${b.lines.join(' · ')}`}
      onClick={go} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
      className="group cursor-pointer outline-none">
      <rect x={x} y={y} width={w} height={h} rx={8} strokeWidth={on ? 2 : 1.4}
        className={`transition-colors ${on ? 'fill-brand-50 stroke-brand-500' : `${own ? 'fill-white stroke-slate-400' : 'fill-slate-50 stroke-slate-300'} group-hover:fill-brand-50/60 group-hover:stroke-brand-400 group-focus-visible:stroke-brand-600`}`} />
      {b.tone && <circle cx={x + 14} cy={ty - 4.5} r={4.5} className={DOT[b.tone]} />}
      <text x={x + (b.tone ? 25 : 14)} y={ty} className="fill-slate-900 text-[13.5px] font-semibold">{b.title}</text>
      {b.lines.map((t, i) => (
        <text key={i} x={x + 14} y={ty + gap + i * 16} className="fill-slate-500 text-[11.5px]">{t}</text>
      ))}
      {roles.map((r, i) => (
        <circle key={r} cx={x + w - 11 - i * 10} cy={y + 11} r={3.5} fill={ROLES[r].color}><title>{ROLES[r].label}</title></circle>
      ))}
      {own && (
        <>
          <circle cx={x} cy={y} r={10} className="fill-slate-800" />
          <text x={x} y={y + 4} textAnchor="middle" className="fill-white text-[11px] font-semibold">{NUM[b.step]}</text>
        </>
      )}
    </g>
  )
}

function FlowMap({ sel }) {
  return (
    <svg viewBox={`0 0 632 ${VIEW_H}`} className="block h-auto w-full min-w-[560px]"
      aria-label="แผนผังขั้นตอนรากเทียม: ทางเข้า → ใครอยู่ในลิสต์ → เลือกลิสต์ → รีวิว → โทร → รอวันนัด → วันนัด → จบรอบ → รอบใหม่">
      <defs>
        <marker id="imp-ah" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" className="fill-slate-400" />
        </marker>
        <marker id="imp-ah-back" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" className="fill-brand-500" />
        </marker>
      </defs>
      {EDGES.map(([kind, d], i) => (
        <path key={i} d={d} fill="none" strokeWidth={1.6} className={STROKE[kind]}
          strokeDasharray={kind === 'back' ? '6 4' : undefined}
          markerEnd={kind === 'go' ? 'url(#imp-ah)' : kind === 'back' ? 'url(#imp-ah-back)' : undefined} />
      ))}
      {JOINS.map(([kind, cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={3.5} className={kind === 'back' ? 'fill-brand-500' : 'fill-slate-400'} />
      ))}
      {LABELS.map(([x, y, key, anchor, kind]) => (
        <text key={key} x={x} y={y} textAnchor={anchor}
          className={`text-[11.5px] font-medium ${kind === 'back' ? 'fill-brand-600' : 'fill-slate-500'}`}>{MAP_LABELS[key]}</text>
      ))}
      {Object.keys(BOX).map((id) => <MapBox key={id} id={id} sel={sel} />)}
    </svg>
  )
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-slate-600">
      <span className="inline-flex items-center gap-1.5">
        <svg width="26" height="8" aria-hidden><path d="M1 4 H25" strokeWidth={1.6} className="stroke-slate-400" /></svg>{MAP_LEGEND.go}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <svg width="26" height="8" aria-hidden><path d="M1 4 H25" strokeWidth={1.6} strokeDasharray="6 4" className="stroke-brand-500" /></svg>{MAP_LEGEND.back}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-slate-800 text-[10.5px] font-semibold text-white">1</span>{MAP_LEGEND.badge}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="flex gap-0.5">
          {Object.values(ROLES).map((r) => <span key={r.label} className="h-[7px] w-[7px] rounded-full" style={{ background: r.color }} />)}
        </span>{MAP_LEGEND.roles}
      </span>
    </div>
  )
}

/** The open step: who, what to do, why — and the way to the step before and after. */
function StepDrawer({ sel, onClose }) {
  const open = useContext(OpenCtx)
  const k = sel?.step
  const s = STEP[k]

  // Land on the branch the clicked box stands for; otherwise on the top of the step.
  // Instant, not smooth: the drawer is still sliding in, and a smooth scroll started
  // under that animation can stop short.
  useEffect(() => {
    if (!sel) return
    document.getElementById(anchor(sel.step, sel.branch))?.scrollIntoView({ block: 'start' })
  }, [sel])

  if (!s) return null
  const i = STEPS.indexOf(s)
  const prev = STEPS[i - 1]
  const next = STEPS[i + 1]
  return (
    <Drawer open onClose={onClose} title={`ข้อ ${NUM[k]} · ${s.title}`} subtitle={<Rich text={s.summary} />}
      footer={(
        <div className="flex items-center justify-between gap-2">
          {prev
            ? <Button size="sm" variant="secondary" icon={ChevronLeft} onClick={() => open(prev.key)}>ข้อ {NUM[prev.key]} {prev.title}</Button>
            : <span />}
          {next
            ? <Button size="sm" variant="secondary" onClick={() => open(next.key)}>ข้อ {NUM[next.key]} {next.title}<ChevronRight size={15} className="-mr-1" /></Button>
            : <Button size="sm" variant="secondary" onClick={onClose}>ปิด</Button>}
        </div>
      )}>
      <div id={anchor(k)} className="scroll-mt-4 space-y-3">
        <div className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-slate-500">ใครทำ <Who roles={s.who} /></div>
        {BODY[k](sel.branch)}
        <Why items={s.why} />
      </div>
    </Drawer>
  )
}

export default function WorkflowGuide() {
  // ?tab=guide&step=call opens that step on arrival — a link a manager can send about
  // one step. Read once; clicking around never rewrites the URL.
  const [params] = useSearchParams()
  const [sel, setSel] = useState(() => (STEP[params.get('step')] ? { step: params.get('step') } : null))
  const open = (step, branch) => setSel({ step, branch })

  return (
    <OpenCtx.Provider value={open}>
      <div className="grid max-w-[1200px] grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,660px)_minmax(0,1fr)]">
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
            <Legend />
            <Button size="sm" variant="secondary" icon={BookOpen} onClick={() => open('doors')}>อ่านทีละขั้น</Button>
          </div>
          <p className="mb-2 text-[12.5px] text-slate-500">{MAP_LEGEND.hint}</p>
          <div className="-mx-1 overflow-x-auto px-1">
            <FlowMap sel={sel} />
          </div>
        </section>

        {/* The rule, the cast, the scoring and the rules that cost most — beside the map. */}
        <div className="space-y-4">
          <Panel icon={Scale} title="กฎข้อเดียวที่ต้องจำ">
            <ol className="space-y-1.5 text-[13.5px] leading-relaxed text-slate-700">
              {THE_RULE.map((t, i) => <li key={i}><Rich text={t} /></li>)}
            </ol>
          </Panel>
          <Panel icon={Users} title="ใครทำอะไร">
            <dl className="space-y-1.5">
              {Object.entries(ROLES).map(([k, r]) => (
                <div key={k} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <dt className="w-[122px] flex-none"><Badge color={r.color}>{r.label}</Badge></dt>
                  <dd className="min-w-0 flex-1 text-[12.5px] leading-relaxed text-slate-600">{r.does}</dd>
                </div>
              ))}
            </dl>
          </Panel>
          <Panel icon={BarChart3} title="ระบบนับผลงานอย่างไร">
            <Chain items={RESULT_CHAIN} />
            <Bullets items={COUNTING} />
          </Panel>
          <Panel icon={AlertTriangle} title="ต้องทำ / ห้ามทำ">
            <ColHead icon={Check} tone="text-emerald-700">ต้องทำ</ColHead>
            <Bullets items={MUST} />
            <div className="mt-3">
              <ColHead icon={X} tone="text-rose-700">ห้ามทำ</ColHead>
              <Bullets items={NEVER} />
            </div>
          </Panel>
          <p className="text-[12px] text-slate-500">ปรับปรุงล่าสุด {fmtDate(GUIDE_UPDATED)} · ขั้นตอนนี้กำหนดโดยคุณหมอราม — ถ้าหน้างานไม่ตรงกับคู่มือ แจ้งผู้ดูแลระบบ</p>
        </div>
      </div>

      {sel && <StepDrawer sel={sel} onClose={() => setSel(null)} />}
    </OpenCtx.Provider>
  )
}
